// TODO: we probably should not have this service in the admin panel and the forms app; we should put it into
// a separate package; problem currently is, that we also need to move the graphql.service.ts file to the package
import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";

// New schema types for the me query
export interface MePyloCustomer {
  id: string;
  name: string;
  subdomain?: string | null;
}

export interface MePyloApp {
  id: string;
  name: string;
  is_default?: boolean;
  pylo_customer?: {
    data: MePyloCustomer;
  };
}

export interface MeUser {
  id: string;
  email: string;
  pylo_app: {
    data: MePyloApp;
  };
  user_profile?: {
    data: {
      id: string;
      firstname: string;
      lastname: string;
    };
  };
}

export interface MeResponseData {
  authenticaton_method?: string;
  current_user: { data: MeUser };
  my_users: { data: MeUser[] };
}

// Legacy interfaces used by other functions in this service
export interface PyloUser {
  id: string;
  email: string;
  pylo_apps: {
    data: Array<{
      id?: string;
      pylo_customer: {
        data: {
          id: string;
          name: string;
        };
      };
    }>;
  };
  user_profile: {
    data: {
      id: string;
      firstname: string;
      lastname: string;
    };
  };
}

export interface PyloUserProfile {
  id: string;
  firstname: string;
  lastname: string;
  pylo_user: {
    data: {
      id: string;
      email: string;
    };
  };
}

export interface PyloUserProfile {
  id: string;
  firstname: string;
  lastname: string;
  pylo_user: {
    data: {
      id: string;
      email: string;
    };
  };
}

const meQueryDocument = gql`
  query me {
    me {
      authenticaton_method
      current_user {
        data {
          id
          email
          pylo_app {
            data {
              id
              name
              pylo_customer {
                data {
                  id
                  name
                  subdomain
                }
              }
            }
          }
          user_profile {
            data {
              id
              firstname
              lastname
            }
          }
        }
      }
      my_users {
        data {
          id
          email
          pylo_app {
            data {
              id
              name
              is_default
              pylo_customer {
                data {
                  id
                  name
                  subdomain
                }
              }
            }
          }
        }
      }
    }
  }
`;

const meQueryDocumentWithoutUserProfile = gql`
  query me {
    me {
      authenticaton_method
      current_user {
        data {
          id
          email
          pylo_app {
            data {
              id
              name
              pylo_customer {
                data {
                  id
                  name
                  subdomain
                }
              }
            }
          }
        }
      }
      my_users {
        data {
          id
          email
          pylo_app {
            data {
              id
              name
              is_default
              pylo_customer {
                data {
                  id
                  name
                  subdomain
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getMyUser = async ({ fetchUserProfile = true }: { fetchUserProfile: boolean }) => {
  const client = createGraphQLClient({ endpointName: "me" });

  try {
    const myUser = (await client.request(
      fetchUserProfile ? meQueryDocument : meQueryDocumentWithoutUserProfile,
    )) as { me: MeResponseData };

    return myUser.me;
  } catch (error) {
    console.error("error fetching pylo user", error);
    throw error;
  }
};

export interface PyloUserWithArosAndPermissons {
  current_user: {
    data: {
      id: string;
      pylo_aros: {
        data: Array<{
          pylo_permissions?: {
            data: Array<{
              pylo_aco?: {
                data: {
                  id: string;
                  name: string;
                };
              };
              has_create: boolean;
              has_update: boolean;
              has_delete: boolean;
            }>;
          };
        }>;
      };
    };
  };
}

export const getMyUserWitArosAndPermissons = async () => {
  const client = createGraphQLClient({
    endpointName: "meWithArosAndPermissons",
  });

  const meWithArosAndPermissionsQuery = gql`
    query meWithArosAndPermissions {
      me {
        current_user {
          data {
            id
            pylo_aros {
              data {
                pylo_permissions {
                  data {
                    pylo_aco {
                      data {
                        id
                        name
                      }
                    }
                    has_create
                    has_update
                    has_delete
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  try {
    const res = (await client.request(meWithArosAndPermissionsQuery)) as {
      me: PyloUserWithArosAndPermissons;
    };

    return res.me;
  } catch (error) {
    console.error("error fetching pylo user", error);
    throw error;
  }
};

const updateMyUserProfileMutationDocument = gql`
  mutation updateMyProfile($input: UpdateUserProfileInput!) {
    updateUserProfile(input: $input) {
      data {
        id
        firstname
        lastname
        pylo_user {
          data {
            id
            email
          }
        }
      }
    }
  }
`;

export const updateMyUserProfile = async (input: {
  id: string;
  firstname?: string;
  lastname?: string;
}) => {
  const client = createGraphQLClient({ endpointName: "updateUserProfile" });

  try {
    const result = (await client.request(updateMyUserProfileMutationDocument, {
      input,
    })) as {
      updateUserProfile: {
        data: PyloUserProfile;
      };
    };

    return result.updateUserProfile.data;
  } catch (error) {
    console.error("error updating user profile", error);
    throw error;
  }
};

const updatePasswordMutationDocument = gql`
  mutation updatePassword($input: UpdatePasswordInput!) {
    updatePassword(input: $input) {
      data {
        success
      }
    }
  }
`;

export const updatePassword = async (input: { currentPassword: string; newPassword: string }) => {
  const client = createGraphQLClient();

  try {
    const result = (await client.request(updatePasswordMutationDocument, {
      input,
    })) as {
      updatePassword: {
        data: {
          success: boolean;
        };
      };
    };

    return result.updatePassword.data?.success;
  } catch (error) {
    console.error("error updating password", error);
    throw error;
  }
};

export const createMyUserProfile = async (input: { firstname: string; lastname: string }) => {
  const myUser = await getMyUser({ fetchUserProfile: false });

  const createMyUserProfileMutationDocument = gql`
    mutation createMyProfile($userId: ID!, $firstname: String!, $lastname: String!) {
      createUserProfile(
        input: { pylo_user_set: { id: $userId }, firstname: $firstname, lastname: $lastname }
      ) {
        data {
          id
          firstname
          lastname
          pylo_user {
            data {
              id
              email
            }
          }
        }
      }
    }
  `;

  const client = createGraphQLClient();
  const createMyUserProfileMutation = (await client.request(createMyUserProfileMutationDocument, {
    userId: myUser.current_user.data.id,
    firstname: input.firstname,
    lastname: input.lastname,
  })) as {
    createUserProfile: {
      data: PyloUserProfile;
    };
  };

  return createMyUserProfileMutation.createUserProfile.data;
};

const updatePyloUserMutationDocument = gql`
  mutation updatePyloUser($input: UpdatePyloUserInput!) {
    updatePyloUser(input: $input) {
      data {
        id
        email
        pylo_apps {
          data {
            pylo_customer {
              data {
                id
                name
              }
            }
          }
        }
        user_profile {
          data {
            id
            firstname
            lastname
          }
        }
      }
    }
  }
`;

export const updatePyloUser = async (input: { id: string; email?: string; password?: string }) => {
  const client = createGraphQLClient();

  try {
    const result = (await client.request(updatePyloUserMutationDocument, {
      input,
    })) as {
      updatePyloUser: {
        data: PyloUser;
      };
    };

    return result.updatePyloUser.data;
  } catch (error) {
    console.error("error updating pylo user", error);
    throw error;
  }
};
